import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import React from "react";
import hoistNonReactStatics from 'hoist-non-react-statics';
import universalComponent from 'react-universal-component';
import { useId } from '@nerdwallet/react-accessibility';
import AccessibilitySSRProvider from '@nerdwallet/react-accessibility-provider';
/**
 * Wrapper around faceyspacey's react-universal-component that renders a react-accessibility-provider around every codesplit component boundary
 * It does this so to ensure that codesplit component trees do not autogenerate ids that could clash or change depending on the timing of the rendering of that particular part of the tree.
 * See comment block at bottom of the file for a more detailed example.
 *
 * @param {*} asyncComponent - a dynamic import statement that resolves with a react Component definition passed to faceyspacey's react-universal-component
 * @param {*} options - options passed to faceyspacey's react-universal-component
 */

var universalComponentWithNerdwalletAccessibilityIntegration = function universalComponentWithNerdwalletAccessibilityIntegration(asyncComponent, options) {
  var UniversalComponent = universalComponent(asyncComponent, _objectSpread({
    // ignore babel renames to make sure server and client bundle names are the same
    // https://github.com/faceyspacey/babel-plugin-universal-import/issues/77#issuecomment-462768369
    ignoreBabelRename: true
  }, options));

  var UniversalComponentWithNerdwalletAccessibilityIntegration = function UniversalComponentWithNerdwalletAccessibilityIntegration(props) {
    var asyncAccessibilityProviderPrefix = useId();
    return /*#__PURE__*/React.createElement(AccessibilitySSRProvider, {
      prefix: asyncAccessibilityProviderPrefix
    }, /*#__PURE__*/React.createElement(UniversalComponent, props));
  }; // keep any static defs from faceyspacey's UniversalComponent
  // (structured-content-core relies on UniversalComponent.preload()) for ssr timing


  hoistNonReactStatics(UniversalComponentWithNerdwalletAccessibilityIntegration, UniversalComponent);
  return UniversalComponentWithNerdwalletAccessibilityIntegration;
};

export default universalComponentWithNerdwalletAccessibilityIntegration; // This came from a discussion we were having about the `async component loading` + react suspense/concurrent issue over in another PR.
// - https://github.com/NerdWallet/react-components/pull/2377#discussion_r479520116
//
// because of that uncertainty,
// wrapping a codesplit boundary with its own prefix
// keeps the ids in that space always clash-free
//
// i.e. consider an App with 5 components (A, B, C, D, E), each of them calling useId once to autogen an id.
//
// ```
// App
//   A: CLIENT_ID-1
//   B: CLIENT_ID-2
//   C: CLIENT_ID-3
//     (async) D: CLIENT_ID-4-1
//        A: CLIENT_ID-4-2
//        B: CLIENT_ID-4-3
//   (async) E: CLIENT_ID-5-1
//      D: CLIENT_ID-5-2
//        A: CLIENT_ID-5-3
//        B: CLIENT_ID-5-4
// ```
//
// each `(async)` boundary calls `useId` where it is deterministic,
// and uses that unique-id as the prefix,
// and starts a new counter for everything down below.
// i.e. `(async) D` and `(async) E` timing of module load and render does not matter.
// E could load before D or vice-versa